import { useEffect, useState } from 'react';

import { DBCategory } from '@core/types/types.db';
import { CategoryUpdateEndpoint } from '@core/types/types.endpoint.category';
import { DirectoryRefreshEndpoint } from '@core/types/types.endpoint.directory';
import { Button } from '@frontend/components/ui/button';
import { Checkbox } from '@frontend/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog';
import { Input } from '@frontend/components/ui/input';
import { Label } from '@frontend/components/ui/label';
import { useDirectoryUpdater } from '@frontend/shared/hooks/shared.hook.useDirectoryUpdater';
import { useDirectoryTeam } from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

interface CategoryEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  category: DBCategory;
}

export function CategoryEditModal({
  isOpen,
  onClose,
  onSuccess,
  category,
}: CategoryEditModalProps) {
  const [name, setName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [nameError, setNameError] = useState('');
  const updateDirectory = useDirectoryUpdater();
  const team = useDirectoryTeam();

  const isCurrentlyDefault = team.default_category_id === category.id;

  // Initialize form with category data when modal opens
  useEffect(() => {
    if (isOpen && category) {
      setName(category.name);
      setMakeDefault(false);
      setNameError('');
    }
  }, [isOpen, category]);

  const submitter = useSubmitter(async () => {
    // Reset errors
    setNameError('');

    // Validate name
    if (!name.trim()) {
      setNameError('Category name is required');
      return;
    }

    if (name.trim().length < 2) {
      setNameError('Category name must be at least 2 characters');
      return;
    }

    const [error] = await callApi<CategoryUpdateEndpoint>(
      '/categories/update',
      {
        id: category.id,
        name: name.trim(),
        makeDefault: makeDefault,
      },
    );

    if (error) {
      if (error.error.includes('already exists')) {
        setNameError('A category with this name already exists');
      } else {
        setNameError(error.error || 'Failed to update category');
      }
      return;
    }

    // Refresh categories and team in directory
    const [, refreshRes] = await callApi<DirectoryRefreshEndpoint>(
      '/directory/refresh',
      { categories: true, team: true },
    );
    if (refreshRes) {
      updateDirectory(refreshRes);
    }

    onSuccess();
  });

  const handleClose = () => {
    if (!submitter.isSubmitting) {
      setNameError('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Category</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="category-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter category name"
              maxLength={50}
              className={nameError ? 'border-red-500' : ''}
            />
            {nameError && <p className="text-sm text-red-500">{nameError}</p>}
          </div>
          {!isCurrentlyDefault && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="make-default"
                checked={makeDefault}
                onCheckedChange={(checked) => setMakeDefault(!!checked)}
              />
              <Label htmlFor="make-default" className="cursor-pointer">
                Make default category
              </Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" className="mr-3" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={submitter.submit}
            disabled={!name.trim() || submitter.isSubmitting}
          >
            {submitter.isSubmitting ? 'Updating...' : 'Update Category'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
